/**
 * Shared date/time parsing and formatting utilities (VIS-136).
 *
 * Value contract used across the app (matches native input values so call
 * sites keep their existing data handling):
 * - datetime fields: 'YYYY-MM-DDTHH:mm' (local wall time, like <input type="datetime-local">)
 * - date fields: 'YYYY-MM-DD' (like <input type="date">)
 */

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

const isValidYmd = (year: number, month: number, day: number): boolean => {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
  );
};

const normalizeYear = (year: number): number => (year < 100 ? 2000 + year : year);

interface ParsedTime {
  hours: number;
  minutes: number;
}

/** Parse a time fragment like '15:30', '3:30 PM', '3pm', '09:05'. */
const parseTimeFragment = (text: string): ParsedTime | null => {
  const match = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)?$/i.exec(text.trim());
  if (!match) {
    return null;
  }
  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3]?.toLowerCase();

  if (minutes > 59) {
    return null;
  }
  if (meridiem) {
    if (hours < 1 || hours > 12) {
      return null;
    }
    if (meridiem.startsWith('p') && hours !== 12) {
      hours += 12;
    }
    if (meridiem.startsWith('a') && hours === 12) {
      hours = 0;
    }
  } else if (hours > 23) {
    return null;
  }
  return { hours, minutes };
};

interface ParsedDateParts {
  year: number;
  month: number;
  day: number;
}

/** Parse a date fragment like '06/15/2026', '6-5-26', '2026-06-15'. */
const parseDateFragment = (text: string): ParsedDateParts | null => {
  const trimmed = text.trim();

  // ISO: YYYY-MM-DD
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (iso) {
    const [, y, m, d] = iso;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    const day = parseInt(d, 10);
    return isValidYmd(year, month, day) ? { year, month, day } : null;
  }

  // US: M/D/YYYY or M-D-YY
  const us = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(trimmed);
  if (us) {
    const month = parseInt(us[1], 10);
    const day = parseInt(us[2], 10);
    const year = normalizeYear(parseInt(us[3], 10));
    return isValidYmd(year, month, day) ? { year, month, day } : null;
  }

  return null;
};

/**
 * Forgiving parse of a typed date+time string into a local Date.
 * Accepts ISO ('2026-06-15T15:30', '2026-06-15 15:30'), US ('06/15/2026 3:30 PM',
 * '6/15/26 3pm') and date-only inputs (time defaults to midnight).
 * Returns null when the text cannot be parsed.
 */
export function parseDateTimeInput(text: string): Date | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }

  // Split into a date part and an optional time part. The separator is 'T',
  // a comma, or the first whitespace after the date token.
  const match = /^(\S+)(?:[T,\s]\s*(.+))?$/.exec(trimmed);
  if (!match) {
    return null;
  }
  // Handle the ISO 'T' separator inside the first token
  let datePart = match[1];
  let timePart = match[2];
  const tIndex = datePart.indexOf('T');
  if (tIndex > 0 && !timePart) {
    timePart = datePart.slice(tIndex + 1);
    datePart = datePart.slice(0, tIndex);
  }

  const dateParts = parseDateFragment(datePart);
  if (!dateParts) {
    return null;
  }

  let time: ParsedTime = { hours: 0, minutes: 0 };
  if (timePart) {
    const parsedTime = parseTimeFragment(timePart);
    if (!parsedTime) {
      return null;
    }
    time = parsedTime;
  }

  return new Date(dateParts.year, dateParts.month - 1, dateParts.day, time.hours, time.minutes);
}

/** Forgiving parse of a typed date string (no time) into a local Date. */
export function parseDateInput(text: string): Date | null {
  const dateParts = parseDateFragment(text);
  if (!dateParts) {
    return null;
  }
  return new Date(dateParts.year, dateParts.month - 1, dateParts.day);
}

/** Format a Date as a datetime-local input value ('YYYY-MM-DDTHH:mm', local time). */
export function toDateTimeLocalValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Convert a datetime-local wall-time value ('YYYY-MM-DDTHH:mm', browser-local)
 * into a UTC ISO instant for the API. Without this the naive wall string is
 * stamped as UTC by the server and re-rendered browser-local, shifting the time
 * by the browser's UTC offset (VIS scheduling bug). Idempotent for values that
 * already carry a timezone (e.g. '...Z'); empty/unparseable values pass through
 * unchanged so server-side validation still runs.
 */
export function toUtcIso(value: string | Date | undefined | null): string | undefined {
  if (!value) {
    return undefined;
  }
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? String(value) : date.toISOString();
}

/** Format a Date as a date input value ('YYYY-MM-DD', local time). */
export function toDateValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const parseValueToDate = (value: string | Date): Date | null => {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  return parseDateTimeInput(value);
};

/** Format a datetime value ('YYYY-MM-DDTHH:mm' or Date) for display: 'MM/DD/YYYY h:mm AM/PM'. */
export function formatDateTimeDisplay(value: string | Date): string {
  if (!value) {
    return '';
  }
  const date = parseValueToDate(value);
  if (!date) {
    return '';
  }
  const hours24 = date.getHours();
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${hours12}:${pad(date.getMinutes())} ${meridiem}`;
}

/** Format a date value ('YYYY-MM-DD' or Date) for display: 'MM/DD/YYYY'. */
export function formatDateDisplay(value: string | Date): string {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : parseDateInput(value);
  if (!date || isNaN(date.getTime())) {
    return '';
  }
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
}

/**
 * Human-readable distance from now: 'in 3h 20m', '1h ago', 'in 2d', 'now'.
 * Returns '' for empty/unparseable input.
 */
export function formatRelativeToNow(value: string | Date): string {
  if (!value) {
    return '';
  }
  const date = parseValueToDate(value);
  if (!date) {
    return '';
  }

  const diffMinutes = Math.round((date.getTime() - Date.now()) / 60000);
  if (diffMinutes === 0) {
    return 'now';
  }

  const future = diffMinutes > 0;
  const total = Math.abs(diffMinutes);
  const days = Math.floor(total / (60 * 24));
  const hours = Math.floor((total % (60 * 24)) / 60);
  const minutes = total % 60;

  let span: string;
  if (days > 0) {
    span = hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  } else if (hours > 0) {
    span = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  } else {
    span = `${minutes}m`;
  }

  return future ? `in ${span}` : `${span} ago`;
}
